/*
    Frontend anti-spam gate.

    Tracks submission attempts per action for the current browser (localStorage,
    survives new tabs) and decides when a captcha must be solved before the next
    request goes out.

    This is a client-side obstacle only — it stops accidental double submits and
    naive browser bots. A server-side limit by IP is still required for real
    protection.
*/

export const ACTIONS = {
    SUPPORT_TICKET: 'support_ticket',
    PKOIN_PURCHASE: 'pkoin_purchase'
}

const MIN_INTERVAL_MS = 10000   // hard minimum between any two submits
const WINDOW_MS = 300000        // 5 minutes
const MAX_IN_WINDOW = 3         // captcha from the 3rd submit inside the window

const COOLDOWN_STEPS_MS = [15000, 30000, 60000, 120000] // escalating captcha-failure cooldown

const CLIENT_ID_KEY = 'pkoin_client_id'
const SESSION_ID_KEY = 'pkoin_session_id'
const STATE_PREFIX = 'pkoin_rl_'

function makeId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/*
    Storage may be unavailable (private mode, disabled cookies, quota).
    Never throw — fall back to an in-memory map so the gate still works
    for the lifetime of the page.
*/
const memory = new Map()

function getStorage(kind) {
    try {
        return window[kind]
    } catch (error) {
        return null
    }
}

function safeGet(kind, key) {
    const storage = getStorage(kind)

    if (storage) {
        try {
            const value = storage.getItem(key)
            if (value !== null) return value
        } catch (error) {
            // storage unavailable — fall through to memory
        }
    }

    return memory.has(key) ? memory.get(key) : null
}

function safeSet(kind, key, value) {
    memory.set(key, value)

    const storage = getStorage(kind)

    if (!storage) return

    try {
        storage.setItem(key, value)
    } catch (error) {
        // storage unavailable — the in-memory copy above is our fallback
    }
}

function emptyState() {
    return { ts: [], failures: 0, cooldownUntil: 0 }
}

class RateLimiter {
    constructor() {
        this.clientId = this.readId('localStorage', CLIENT_ID_KEY)
        this.sessionId = this.readId('sessionStorage', SESSION_ID_KEY)
    }

    readId(kind, key) {
        let id = safeGet(kind, key)

        if (!id) {
            id = makeId()
            safeSet(kind, key, id)
        }

        return id
    }

    getState(action) {
        const raw = safeGet('localStorage', STATE_PREFIX + action)

        if (!raw) return emptyState()

        try {
            const parsed = JSON.parse(raw)

            return {
                ts: Array.isArray(parsed.ts) ? parsed.ts : [],
                failures: Number(parsed.failures) || 0,
                cooldownUntil: Number(parsed.cooldownUntil) || 0
            }
        } catch (error) {
            return emptyState()
        }
    }

    setState(action, state) {
        safeSet('localStorage', STATE_PREFIX + action, JSON.stringify(state))
    }

    /*
        Returns { allowed, needCaptcha, waitSeconds }.
        needCaptcha means: not allowed right now, but solving the puzzle unblocks it.
    */
    check(action) {
        const now = Date.now()
        const state = this.getState(action)

        if (state.cooldownUntil > now) {
            return { allowed: false, needCaptcha: false, waitSeconds: Math.ceil((state.cooldownUntil - now) / 1000) }
        }

        const recent = state.ts.filter(item => now - item < WINDOW_MS)
        const last = recent.length ? recent[recent.length - 1] : 0

        if (last && now - last < MIN_INTERVAL_MS) {
            return { allowed: false, needCaptcha: false, waitSeconds: Math.ceil((MIN_INTERVAL_MS - (now - last)) / 1000) }
        }

        if (recent.length >= MAX_IN_WINDOW) {
            return { allowed: false, needCaptcha: true, waitSeconds: 0 }
        }

        return { allowed: true, needCaptcha: false, waitSeconds: 0 }
    }

    // Called at the moment the real request fires, not on button click.
    recordAttempt(action) {
        const now = Date.now()
        const state = this.getState(action)

        state.ts = state.ts.filter(item => now - item < WINDOW_MS)
        state.ts.push(now)

        this.setState(action, state)
    }

    creditSolvedCaptcha(action) {
        this.setState(action, emptyState())
    }

    recordCaptchaFailure(action) {
        const state = this.getState(action)

        state.failures += 1

        const step = COOLDOWN_STEPS_MS[Math.min(state.failures - 1, COOLDOWN_STEPS_MS.length - 1)]
        state.cooldownUntil = Date.now() + step

        this.setState(action, state)
    }
}

export default new RateLimiter()
